import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { appToastContext } from "../../context/toast";
import { Panel } from "primereact/panel";
import { userContext } from "../../context/user";
import {
  CreateAccountPayload,
  UpdateAccountPayload,
} from "../../api/types/account";
import { createAccountRequest } from "../../api/queries/account/create-account";
import { updateAccountRequest } from "../../api/queries/account/update-account";
import { accountRequest } from "../../api/queries/account/account";
import { enableAccountRequest } from "../../api/queries/account/enable-account";
import { disableAccountRequest } from "../../api/queries/account/disable-account";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { Institution } from "../../api/types/institution";
import { useDebounce } from "../../common/useDebounce";
import { useInstitutionsSearch } from "../../api/queries/institutions/useInstitutionsSearch";

export default function AccountForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ref: appToastRef } = useContext(appToastContext);
  const { isAdmin } = useContext(userContext);

  const createAccount = useMutation({
    mutationFn: (payload: CreateAccountPayload) =>
      createAccountRequest(payload),
  });

  const updateAccount = useMutation({
    mutationFn: (payload: UpdateAccountPayload) =>
      updateAccountRequest(uuid, payload),
  });

  const enableAccount = useMutation({
    mutationFn: () => enableAccountRequest(uuid),
  });

  const disableAccount = useMutation({
    mutationFn: () => disableAccountRequest(uuid),
  });

  const { data: account } = useQuery({
    queryFn: () => accountRequest(uuid),
    queryKey: queryKeys.account(uuid),
    enabled: Boolean(uuid),
  });

  const [active, setActive] = useState(false);

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [institutionId, setInstitutionId] = useState<null | string>(null);

  const [institutionName, setInstitutionName] = useState<string>("");
  const onInstitutionNameChange = (event: AutoCompleteChangeEvent): void => {
    if (typeof event.value === "string") {
      setInstitutionId(null);
      setInstitutionName(event.value);
    } else {
      const institution: Institution = event.value;
      setInstitutionId(institution.id);
      setInstitutionName(institution.name);
    }
  };
  const institutionNameDebounced = useDebounce(institutionName, 500);

  const { data: institutionsResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 5,
    search: institutionNameDebounced,
  });
  const [institutionOptions, setInstitutionOptions] = useState<Institution[]>(
    [],
  );

  useEffect(() => {
    if (institutionsResponse) {
      setInstitutionOptions(institutionsResponse.data.results);
    }
  }, [institutionsResponse]);

  const onInstitutionSearchComplete = () => {};

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();
    if (uuid) {
      const payload: UpdateAccountPayload = {
        active,
        name,
        institution: institutionId as string,
      };

      await updateAccount.mutateAsync(payload);
      queryClient.invalidateQueries(queryKeys.account(uuid));
    } else {
      const payload: CreateAccountPayload = {
        active: false,
        name,
        institution: institutionId as string,
      };

      await createAccount.mutateAsync(payload);
    }

    queryClient.invalidateQueries(queryKeys.accountList(), {
      refetchInactive: true,
    });

    navigate("/accounts");
  };

  useEffect(() => {
    if (account) {
      setActive(account.data.active);
      setName(account.data.name);
      setInstitutionId(account.data.institution.id);
      setInstitutionName(account.data.institution.name);
    }
  }, [account]);

  const onEnable = async () => {
    await enableAccount.mutateAsync();

    queryClient.invalidateQueries(queryKeys.account(uuid));
    queryClient.invalidateQueries(queryKeys.accountList(), {
      refetchInactive: true,
    });

    appToastRef?.current?.show({
      summary: "Account enabled.",
      severity: "success",
    });

    navigate("/accounts");
  };

  const onDisable = async () => {
    await disableAccount.mutateAsync();

    queryClient.invalidateQueries(queryKeys.account(uuid));
    queryClient.invalidateQueries(queryKeys.accountList(), {
      refetchInactive: true,
    });

    appToastRef?.current?.show({
      summary: "Account disabled.",
      severity: "success",
    });

    navigate("/accounts");
  };

  const onCancel = () => navigate("/accounts");

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="Account" toggleable={true} className="mb-3">
        <div className="p-fluid">
          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <InputText value={name} onChange={onNameChange} />
                <label>Name *</label>
              </div>
            </div>
          </div>

          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <AutoComplete
                  value={institutionName}
                  suggestions={institutionOptions}
                  completeMethod={onInstitutionSearchComplete}
                  onChange={onInstitutionNameChange}
                  field="name"
                />
                <label>Select Institution *</label>
              </div>
            </div>
          </div>
        </div>
      </Panel>

      <div className="flex gap-1 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-sm p-button-secondary"
          text={true}
          onClick={onCancel}
        />

        {uuid && isAdmin() && (
          <>
            {account?.data.active ? (
              <Button
                type="button"
                label="Disable"
                outlined={true}
                className="p-button-sm p-button-danger"
                onClick={onDisable}
              />
            ) : (
              <Button
                type="button"
                label="Enable"
                className="p-button-sm p-button-success"
                onClick={onEnable}
              />
            )}
          </>
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          disabled={!name || !institutionId}
        />
      </div>
    </form>
  );
}
